// Central JSON-LD schema definitions for CargoNova

// Organization schema (global on all pages)
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://cargonova.ai/#organization",
  name: "CargoNova",
  url: "https://cargonova.ai/",
  logo: "https://cargonova.ai/logo.png",
  description: "AI-native freight forwarding platform by Deep Insights",
  parentOrganization: {
    "@type": "Organization",
    name: "Deep Insights",
    url: "https://www.deep-insights.ai/",
  },
  sameAs: [
    "https://www.linkedin.com/company/deep-insights-ai",
    "https://www.linkedin.com/company/deep-insights-technology/",
    "https://twitter.com/cargonova",
  ],
  // Light-weight geo / brand context
  address: {
    "@type": "PostalAddress",
    streetAddress: "Lujiazui Financial Center, Pudong",
    addressLocality: "Shanghai",
    addressRegion: "Shanghai",
    addressCountry: "CN",
  },
  areaServed: "Worldwide",
};

// WebSite schema (global, also associated with home)
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://cargonova.ai/#website",
  name: "CargoNova",
  url: "https://cargonova.ai/",
  description: "AI-native freight forwarding platform by Deep Insights",
  publisher: {
    "@id": "https://cargonova.ai/#organization",
  },
  inLanguage: "en",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: "https://cargonova.ai/search?q={search_term_string}",
    },
    "query-input": "required name=search_term_string",
  },
};

// SoftwareApplication schema (home page only)
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": "https://cargonova.ai/#software",
  name: "CargoNova",
  alternateName: ["CargoNova AI", "Cargo Nova", "Cargo Nova AI"],
  url: "https://cargonova.ai/",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "CargoNova AI is an AI-native freight forwarding software platform by Deep Insights, covering ocean/air freight, smart pricing, quoting, booking, documents, customs and automated settlement.",
  keywords:
    "freight forwarding software, AI freight platform, CargoNova AI freight management, CargoNova freight forwarding software",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
  publisher: {
    "@id": "https://cargonova.ai/#organization",
  },
  author: {
    "@id": "https://cargonova.ai/#organization",
  },
};


// FAQPage schema (home page — AI answer engines quote these directly)
export const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://cargonova.ai/#faq",
  inLanguage: "en",
  isPartOf: { "@id": "https://cargonova.ai/#website" },
  mainEntity: [
    {
      "@type": "Question",
      name: "What is CargoNova AI?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova AI is the AI-native freight forwarding software platform from Deep Insights, published at https://cargonova.ai/. CargoNova, also written as Cargo Nova, refers to the same platform, which uses AI agents to automate quoting, booking, documentation, customs and settlement.",
      },
    },
    {
      "@type": "Question",
      name: "What is CargoNova?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova is an AI-native freight forwarding platform by Deep Insights. It combines a cloud-native core system with specialized AI agents that automate quoting, pricing, booking, documentation, customs and settlement for ocean and air freight forwarders.",
      },
    },
    {
      "@type": "Question",
      name: "How is CargoNova different from CargoWise?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova is an AI-native, AI-agent-first freight platform, while CargoWise is a traditional, module-heavy TMS. Forwarders often evaluate CargoNova as a modern CargoWise alternative when they want faster deployment, lighter configuration and built-in AI automation.",
      },
    },
    {
      "@type": "Question",
      name: "What types of freight forwarders use CargoNova?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova is used by local NVOCCs, Southeast Asia freight forwarders and global logistics groups handling ocean and air freight. It fits companies that want to move beyond spreadsheets and legacy tools into an AI-powered freight forwarding software platform.",
      },
    },
    {
      "@type": "Question",
      name: "How do CargoNova AI agents automate freight operations?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova provides specialized AI agents for rate optimization, quoting, booking, documentation, customs and settlement. They read data, trigger workflows and write results back to your systems so human operators can focus on exceptions and customer service.",
      },
    },
    {
      "@type": "Question",
      name: "Is CargoNova suitable for Southeast Asia freight forwarders?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Yes. CargoNova is optimized for Southeast Asia freight forwarders with multi-language support, multi-currency billing and regional customs practices, making it a strong fit for forwarders in Singapore, Thailand, Vietnam, Indonesia and Malaysia.",
      },
    },
    {
      "@type": "Question",
      name: "How does CargoNova handle customs compliance?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova includes an AI customs worker that suggests HS codes, performs sanctions screening, validates origin and license data and records compliance results alongside each shipment, helping reduce delays and penalties.",
      },
    },
    {
      "@type": "Question",
      name: "What shipping modes does CargoNova support?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova mainly supports ocean and air freight forwarding, including FCL, LCL, air consolidation and direct air shipments, and can integrate with other systems to support multimodal and door-to-door logistics flows.",
      },
    },
    {
      "@type": "Question",
      name: "Can I migrate from CargoWise to CargoNova?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "Yes. Many forwarders migrate from CargoWise or other legacy TMS platforms to CargoNova using a phased approach that moves master data and key workflows while keeping operations stable.",
      },
    },
    {
      "@type": "Question",
      name: "How much does CargoNova cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text:
          "CargoNova is priced as a cloud platform with a base subscription plus usage-based components. Final pricing depends on users, offices, enabled AI agents, shipment volumes and integrations, and is confirmed in a tailored proposal.",
      },
    },
  ],
};


// BreadcrumbList for the brand definition page (Home → What is CargoNova)
export const whatIsCargoNovaBreadcrumb = (lang: "en" | "zh" | "th" = "en") => {
  const prefix = lang === "en" ? "" : `/${lang}`;
  const name =
    lang === "zh" ? "什么是 CargoNova" : lang === "th" ? "CargoNova คืออะไร" : "What is CargoNova";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `https://cargonova.ai${prefix}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name,
        item: `https://cargonova.ai${prefix}/what-is-cargonova`,
      },
    ],
  };
};

// BreadcrumbList for the platform features page (Home → Features)
export const featuresBreadcrumb = (lang: "en" | "zh" | "th" = "en") => {
  const prefix = lang === "en" ? "" : `/${lang}`;
  const name =
    lang === "zh" ? "平台功能" : lang === "th" ? "ฟีเจอร์ของแพลตฟอร์ม" : "Features";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `https://cargonova.ai${prefix}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name,
        item: `https://cargonova.ai${prefix}/features`,
      },
    ],
  };
};

// FAQPage schema for the platform features page
const featuresFaqContent: Record<
  "en" | "zh" | "th",
  { q: string; a: string }[]
> = {
  en: [
    {
      q: "What features should an AI freight management system have?",
      a: "At minimum: a live rate engine, AI quoting, booking and milestone tracking, document extraction, customs support and automated settlement with margin reporting. CargoNova covers all six in one platform.",
    },
    {
      q: "Does CargoNova replace my existing systems?",
      a: "No. CargoNova integrates with modern APIs and legacy carrier portals through a See–Calculate–Write loop, so you can adopt it without a rip-and-replace project.",
    },
    {
      q: "Which transport modes does CargoNova support?",
      a: "Ocean freight (FCL and LCL), air freight and cross-border trucking, with multi-currency and multi-language operations.",
    },
  ],
  zh: [
    {
      q: "国际货代 AI 系统应该具备哪些功能？",
      a: "至少应包含实时运价引擎、AI 智能报价、订舱与轨迹跟踪、单证智能识别、关务合规支持，以及自动结算与利润分析。CargoNova 在同一平台内提供上述全部六项能力。",
    },
    {
      q: "CargoNova 会替换我现有的系统吗？",
      a: "不会。CargoNova 通过看-算-写（See–Calculate–Write）闭环，既能对接现代 API，也能操作老旧船司门户，无需推倒重来即可落地。",
    },
    {
      q: "CargoNova 支持哪些运输方式？",
      a: "支持海运（整柜与拼箱）、空运与跨境陆运，并内置多币种、多语言运营能力。",
    },
  ],
  th: [
    {
      q: "ระบบบริหารงานขนส่งด้วย AI ควรมีฟีเจอร์อะไรบ้าง?",
      a: "อย่างน้อยควรมีเอนจินเรทแบบเรียลไทม์ ใบเสนอราคาด้วย AI การจองและติดตามสถานะ การอ่านเอกสาร การรองรับศุลกากร และการกระทบยอดพร้อมรายงานกำไร CargoNova มีครบทั้งหกอย่างในแพลตฟอร์มเดียว",
    },
    {
      q: "CargoNova จะแทนที่ระบบเดิมของเราหรือไม่?",
      a: "ไม่ CargoNova เชื่อมต่อทั้ง API สมัยใหม่และพอร์ทัลระบบเดิมผ่านวงจร ดู-คำนวณ-เขียน จึงเริ่มใช้ได้โดยไม่ต้องรื้อระบบ",
    },
    {
      q: "CargoNova รองรับการขนส่งรูปแบบใดบ้าง?",
      a: "รองรับทางเรือ (FCL และ LCL) ทางอากาศ และรถข้ามแดน พร้อมการทำงานหลายสกุลเงินและหลายภาษา",
    },
  ],
};

export const featuresFaqSchema = (lang: "en" | "zh" | "th" = "en") => {
  const prefix = lang === "en" ? "" : `/${lang}`;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `https://cargonova.ai${prefix}/features#faq`,
    inLanguage: lang,
    mainEntity: featuresFaqContent[lang].map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
};
